import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useSession } from '../lib/session.js';

/* ============================================================================
   The login gate — a courtesy, not a security boundary.
   ----------------------------------------------------------------------------
   Every endpoint behind it checks the portal session itself. This exists so
   that someone who is not logged in sees a login screen instead of a page full
   of errors, which is a different problem from keeping them out.

   While the session is still `loading` it renders nothing rather than
   redirecting. Redirecting on "we have not asked yet" is what sends someone
   back to the login screen every time they refresh a page they are logged into.
   ========================================================================== */

export function RequireClient({ children }: { children: ReactNode }) {
  const { status, retry } = useSession();
  const location = useLocation();

  if (status === 'unavailable') {
    /* NOT a redirect to /login. We do not know whether this session is dead —
       the request never got an answer — and the login screen here asks for a
       fresh WhatsApp code, capped at five an hour. Making someone spend one
       because their bus went through a tunnel is the wrong trade. */
    return (
      <div className="center" role="alert">
        <p>Connection nahi mil raha.</p>
        <p className="muted">Aap logged in hain. Network aate hi dobara try kijiye.</p>
        <button type="button" className="btn" onClick={retry}>
          Dobara try kijiye
        </button>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <p className="muted center" role="status">
        Khul raha hai…
      </p>
    );
  }

  if (status === 'anonymous') {
    /* Where they were going is remembered, so a link shared over WhatsApp
       still lands on the right screen after the code. */
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}
